/* eslint-disable react-refresh/only-export-components -- useToast is colocated with ToastStore */
import React from 'react';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning';

export interface ToastOptions {
  title: string;
  /** Supporting detail. Name the problem and the recovery, not just the failure. */
  description?: string;
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss. Errors stay until dismissed by default. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastRecord extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  toasts: ToastRecord[];
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/** Errors persist until dismissed; everything else clears itself. */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  default: 5000,
  success: 4000,
  warning: 8000,
  error: Infinity,
};

let counter = 0;

export const ToastStore: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = React.useCallback((options: ToastOptions) => {
    const id = `toast-${(counter += 1)}`;
    const variant = options.variant ?? 'default';
    setToasts((prev) => {
      const next = [...prev, { ...options, variant, id }];
      // A queue that grows without bound buries the newest message.
      return next.slice(-3);
    });
    return id;
  }, []);

  const value = React.useMemo(
    () => ({ toasts, toast, dismiss }),
    [toasts, toast, dismiss]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within <ToastStore>');
  }

  const { toast, dismiss, toasts } = context;

  return React.useMemo(
    () => ({
      toasts,
      dismiss,
      toast,
      success: (title: string, description?: string) =>
        toast({ title, description, variant: 'success' }),
      error: (title: string, description?: string) =>
        toast({ title, description, variant: 'error' }),
      warning: (title: string, description?: string) =>
        toast({ title, description, variant: 'warning' }),
    }),
    [toast, dismiss, toasts]
  );
}

export { DEFAULT_DURATION };
